import React, { useCallback, useState } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { useFocusEffect } from '@react-navigation/native';
import type { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AppButton } from '../../components/AppButton';
import { AppCard } from '../../components/AppCard';
import { AppInput } from '../../components/AppInput';
import { AppLoader } from '../../components/AppLoader';
import { AppScreen } from '../../components/AppScreen';
import { ErrorState } from '../../components/ErrorState';
import { PermissionGuard } from '../../components/PermissionGuard';
import { ScreenHeader } from '../../components/ScreenHeader';
import { PERMISSIONS } from '../../constants/permissions';
import type { RootStackParamList } from '../../constants/routes';
import { spacing } from '../../constants/spacing';
import { typography } from '../../constants/typography';
import { useAppColors } from '../../context/PreferencesContext';
import {
  getPermissionOrgPolicy,
  savePermissionOrgPolicy,
} from '../../services/hrPolicyConfigurationService';
import {
  PERMISSION_ORG_POLICY_NOTES_MAX_LENGTH,
  type PermissionOrgPolicy,
} from '../../types/hrPolicyConfiguration';
import { normalizeApiError } from '../../api/apiError';
import {
  DURATION_INPUT_HINT,
  formatMinutes,
  parseDurationFormValue,
} from '../../utils/dateTime';
import { UnauthorizedScreen } from '../system/UnauthorizedScreen';
import { runOperation } from '../../utils/operationFeedback';

type Props = NativeStackScreenProps<
  RootStackParamList,
  'PermissionConfigurationPolicy'
>;

function asText(value: number): string {
  return value > 0 ? String(value) : '';
}

function durationText(value: number): string {
  return value > 0 ? formatMinutes(value) : '';
}

export function validatePermissionOrgPolicyForm(policy: PermissionOrgPolicy): string | null {
  if (
    !Number.isInteger(policy.maximumPermissionsPerMonth) ||
    policy.maximumPermissionsPerMonth < 1
  ) {
    return 'Enter a monthly permission limit of at least 1.';
  }
  if (
    !Number.isInteger(policy.minimumDurationMinutes) ||
    policy.minimumDurationMinutes < 1
  ) {
    return 'Enter a minimum duration using format like 30m or 01h 00m.';
  }
  if (
    !Number.isInteger(policy.maximumDurationMinutes) ||
    policy.maximumDurationMinutes < policy.minimumDurationMinutes
  ) {
    return 'Maximum duration must be at least the minimum duration.';
  }
  if ((policy.notes ?? '').length > PERMISSION_ORG_POLICY_NOTES_MAX_LENGTH) {
    return `Notes cannot exceed ${PERMISSION_ORG_POLICY_NOTES_MAX_LENGTH} characters.`;
  }
  return null;
}

export function PermissionConfigurationPolicyScreen({ navigation }: Props) {
  const palette = useAppColors();
  const [policy, setPolicy] = useState<PermissionOrgPolicy>({
    permissionConfigurationId: null,
    organizationId: null,
    maximumPermissionsPerMonth: 0,
    minimumDurationMinutes: 0,
    maximumDurationMinutes: 0,
    notes: '',
    rowVersion: 1,
  });
  const [minimumDurationText, setMinimumDurationText] = useState('');
  const [maximumDurationText, setMaximumDurationText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const saved = await getPermissionOrgPolicy();
      setPolicy(saved);
      setMinimumDurationText(durationText(saved.minimumDurationMinutes));
      setMaximumDurationText(durationText(saved.maximumDurationMinutes));
      setError(null);
    } catch (caught) {
      setError(normalizeApiError(caught).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useFocusEffect(
    useCallback(() => {
      load().catch(() => undefined);
    }, [load]),
  );

  async function submit() {
    const minimumDurationMinutes = parseDurationFormValue(minimumDurationText) ?? 0;
    const maximumDurationMinutes = parseDurationFormValue(maximumDurationText) ?? 0;
    if (
      parseDurationFormValue(minimumDurationText) == null &&
      minimumDurationText.trim()
    ) {
      setError('Minimum duration must use format like 30m or 01h 00m.');
      return;
    }
    if (
      parseDurationFormValue(maximumDurationText) == null &&
      maximumDurationText.trim()
    ) {
      setError('Maximum duration must use format like 30m or 01h 00m.');
      return;
    }
    const nextPolicy = {
      ...policy,
      minimumDurationMinutes,
      maximumDurationMinutes,
    };
    const validation = validatePermissionOrgPolicyForm(nextPolicy);
    if (validation) {
      setError(validation);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const saved = await runOperation('update', () => savePermissionOrgPolicy({
        maximumPermissionsPerMonth: nextPolicy.maximumPermissionsPerMonth,
        minimumDurationMinutes: nextPolicy.minimumDurationMinutes,
        maximumDurationMinutes: nextPolicy.maximumDurationMinutes,
        notes: (nextPolicy.notes ?? '').trim(),
        rowVersion: nextPolicy.rowVersion,
      }));
      setPolicy(saved);
      setMinimumDurationText(durationText(saved.minimumDurationMinutes));
      setMaximumDurationText(durationText(saved.maximumDurationMinutes));
      navigation.goBack();
    } catch (caught) {
      setError(normalizeApiError(caught).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <PermissionGuard
      permission={PERMISSIONS.SETTINGS_UPDATE}
      fallback={<UnauthorizedScreen />}
    >
      <AppScreen scroll keyboardSafe>
        <ScreenHeader title="Edit permission policy" onBack={navigation.goBack} />
        {loading ? (
          <AppLoader message="Loading permission policy" />
        ) : error && !policy.permissionConfigurationId && policy.maximumPermissionsPerMonth < 1 ? (
          <ErrorState message={error} onRetry={() => load()} />
        ) : (
          <View style={styles.body}>
            <Text style={[styles.caption, { color: palette.textMuted }]}>
              These values are stored in the database and apply overall to each employee,
              for every permission type.
            </Text>
            <AppCard style={styles.card}>
              <AppInput
                label="Maximum permissions per month"
                value={asText(policy.maximumPermissionsPerMonth)}
                onChangeText={value =>
                  setPolicy(current => ({
                    ...current,
                    maximumPermissionsPerMonth: Number(value || 0),
                  }))
                }
                keyboardType="number-pad"
              />
              <AppInput
                label="Minimum duration per permission"
                value={minimumDurationText}
                onChangeText={setMinimumDurationText}
                autoCapitalize="none"
                autoCorrect={false}
                placeholder={DURATION_INPUT_HINT}
              />
              <AppInput
                label="Maximum duration per permission"
                value={maximumDurationText}
                onChangeText={setMaximumDurationText}
                autoCapitalize="none"
                autoCorrect={false}
                placeholder={DURATION_INPUT_HINT}
              />
              <AppInput
                label="Notes"
                value={policy.notes}
                onChangeText={value =>
                  setPolicy(current => ({
                    ...current,
                    notes: value,
                  }))
                }
              />
            </AppCard>
            {error ? (
              <Text accessibilityRole="alert" style={[styles.error, { color: palette.error }]}>
                {error}
              </Text>
            ) : null}
            <AppButton
              title="Save permission policy"
              onPress={submit}
              loading={saving}
            />
          </View>
        )}
      </AppScreen>
    </PermissionGuard>
  );
}

const styles = StyleSheet.create({
  body: { marginTop: spacing.lg, gap: spacing.md, paddingBottom: spacing.xl },
  card: { gap: spacing.md },
  caption: typography.caption,
  error: typography.caption,
});
